import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface ProposalVersion {
  id: string;
  number: string;
  version: string;
  status: string;
  status_display: string;
  total: string;
  responsible: {
    id: number;
    name: string;
    email: string;
  };
  is_latest: boolean;
  created_at: string;
  updated_at: string;
}

interface VersionHistoryResponse {
  count: number;
  current_version: string;
  is_latest: boolean;
  versions: ProposalVersion[];
}

interface VersionHistoryModalProps {
  proposalId: string;
  proposalNumber: string;
  isOpen: boolean;
  onClose: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  proposalId,
  proposalNumber,
  isOpen,
  onClose,
}) => {
  const [versions, setVersions] = useState<ProposalVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [totalVersions, setTotalVersions] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && proposalId) {
      fetchVersions();
    }
  }, [isOpen, proposalId]);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<VersionHistoryResponse>(
        `/api/v1/proposals/${proposalId}/versions/`
      );
      setVersions(response.data.versions);
      setCurrentVersion(response.data.current_version);
      setTotalVersions(response.data.count);
    } catch (err: any) {
      console.error('Erro ao buscar versões:', err);
      setError('Erro ao carregar histórico de versões');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value: string) => {
    const numValue = parseFloat(value);
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const handleViewVersion = (versionId: string) => {
    navigate(`/proposals/${versionId}`);
    onClose();
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      novo_lead: 'bg-blue-100 text-blue-800',
      em_elaboracao: 'bg-yellow-100 text-yellow-800',
      enviada: 'bg-purple-100 text-purple-800',
      aprovada: 'bg-green-100 text-green-800',
      rejeitada: 'bg-red-100 text-red-800',
      expirada: 'bg-gray-100 text-gray-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Histórico de Versões
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              Proposta: <span className="font-semibold">{proposalNumber}</span> •{' '}
              {totalVersions} {totalVersions === 1 ? 'versão' : 'versões'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
                <button
                  onClick={fetchVersions}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Nenhuma versão encontrada
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                    version.is_latest
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {version.number}
                        </h3>
                        {version.is_latest && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                            <CheckCircleIcon className="h-3 w-3" />
                            Versão Atual
                          </span>
                        )}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            version.status
                          )}`}
                        >
                          {version.status_display}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Versão:</span> v
                          {version.version}
                        </div>
                        <div>
                          <span className="font-medium">Total:</span>{' '}
                          {formatCurrency(version.total)}
                        </div>
                        <div>
                          <span className="font-medium">Responsável:</span>{' '}
                          {version.responsible.name}
                        </div>
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          <span className="font-medium">Criada em:</span>{' '}
                          {formatDate(version.created_at)}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleViewVersion(version.id)}
                      className="ml-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      Visualizar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryModal;
